import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import Card from "../componenets/Card";
import MemberBanner from "../componenets/MemberBanner";
import { markPostsSeen } from "../lib/seenPosts";
import { normalizeTag } from "../lib/tags";

const app_hostname = window.location.hostname === "localhost"
    ? "social.ambasada.pro"
    : window.location.hostname;

const parseIds = (raw) => {
    if (!raw) return [];
    const seen = new Set();
    const out = [];
    raw.split(",").forEach((s) => {
        const id = s.trim();
        if (id && !seen.has(id)) {
            seen.add(id);
            out.push(id);
        }
    });
    return out;
};

const toCard = (d) => ({
    id: d.id,
    ...d.data(),
    post_url: `https://${app_hostname}/s/${d.id}`,
});

const CuratedShare = () => {
    const [params] = useSearchParams();
    const tag = useMemo(() => normalizeTag(params.get("tag") || ""), [params]);
    const ids = useMemo(() => parseIds(params.get("ids")), [params]);
    const [media, setMedia] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            if (!tag && ids.length === 0) {
                setLoading(false);
                return;
            }
            try {
                if (tag) {
                    const snap = await getDocs(query(
                        collection(db, "media"),
                        where("tags", "array-contains", tag),
                        orderBy("timestamp", "desc")
                    ));
                    if (cancelled) return;
                    const items = snap.docs.map(toCard).filter((m) => !m.archived);
                    setMedia(items);
                    markPostsSeen(items.map((m) => m.id));
                } else {
                    const snap = await getDocs(collection(db, "media"));
                    if (cancelled) return;
                    const byId = new Map();
                    snap.docs.forEach((d) => byId.set(d.id, toCard(d)));
                    const ordered = ids.map((id) => byId.get(id)).filter(Boolean);
                    setMedia(ordered);
                    markPostsSeen(ordered.map((m) => m.id));
                }
            } catch (err) {
                console.error("Failed to load curated posts", err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => { cancelled = true; };
    }, [tag, ids]);

    const hasSelector = Boolean(tag) || ids.length > 0;

    return (
        <div className="bg-brand-bg min-h-screen">
            <header className="bg-white border-b border-brand-panel">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-8 sm:py-12 lg:py-14">
                    <h1 className="flex flex-wrap items-center gap-x-4 sm:gap-x-5 gap-y-2 font-brand text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-ink leading-[1.05]">
                        <Link to="/" aria-label="Home" className="inline-flex items-center">
                            <img src="/ambasada-mark.svg" alt="Ambasada" width="40" height="40" className="h-12 sm:h-14 lg:h-16 w-auto" />
                        </Link>
                        <span>share these<span className="text-brand-red">.</span></span>
                    </h1>
                    <p className="mt-4 text-base sm:text-lg text-brand-muted max-w-xl">
                        {tag
                            ? <>Posts tagged <span className="font-semibold text-brand-ink">#{tag}</span>. Pick a platform on any card.</>
                            : "A handpicked set, ready to push out. Pick a platform on any card."}
                    </p>
                    <div className="mt-5 h-1 w-24 bg-brand-red rounded-full" />
                </div>
            </header>

            <MemberBanner />

            {!hasSelector && (
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
                    <div className="rounded-2xl bg-white border border-brand-panel p-8 text-sm text-brand-muted text-center">
                        This link is missing post ids or a tag. Ask the sender to resend with the full URL.
                    </div>
                </div>
            )}

            {!loading && hasSelector && media.length === 0 && (
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
                    <div className="rounded-2xl bg-white border border-brand-panel p-8 text-sm text-brand-muted text-center">
                        {tag
                            ? <>No posts tagged <span className="font-semibold">#{tag}</span> right now.</>
                            : "None of those posts are available right now."}
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-6 sm:px-8 py-8 sm:py-10 columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-5">
                {media.map((campaign, index) => (
                    <Card key={campaign.id} campaign={campaign} index={index} track={campaign.track} />
                ))}
            </div>
        </div>
    );
};

export default CuratedShare;
